/**
 * Dialog — the §7.13 modal, on `@radix-ui/react-dialog` (mission 6 WP03 T021;
 * FR-005, FR-027; DM-01M3C4XR0TYMTDR6BNEBZBQGXF).
 *
 * Centred, `--modal-width-sm|md|lg` (default md, §5.1), radius 12,
 * `--shadow-lg`, a full-screen `--overlay` scrim, and never taller than the
 * viewport less 64px (the body scrolls). Enters with a scale from 0.98 over
 * `--duration-slow`; none under reduced motion (§4.5, P21). Radix traps focus,
 * closes on Esc and restores focus to the trigger.
 *
 * The title is an `h2`. `DialogFooter` is right-aligned; callers put the ghost
 * Cancel first, then the primary or danger action. No copy lives here.
 */
import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from './cn'

export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close
export const DialogDescription = DialogPrimitive.Description

const dialogVariants = cva(
  'fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-64px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-lg bg-white text-navy-950 [box-shadow:var(--shadow-lg)] focus:outline-none animate-[trellis-dialog-in_var(--duration-slow)_var(--ease-out)] motion-reduce:animate-none',
  {
    variants: {
      size: {
        sm: 'w-[var(--modal-width-sm)]',
        md: 'w-[var(--modal-width-md)]',
        lg: 'w-[var(--modal-width-lg)]',
      },
    },
    defaultVariants: { size: 'md' },
  },
)

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogVariants> {}

export const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, size, 'aria-describedby': describedBy, children, ...props }, ref) => (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay data-dialog-overlay="" className="fixed inset-0 z-40 bg-[var(--overlay)]" />
      <DialogPrimitive.Content
        ref={ref}
        aria-describedby={describedBy}
        className={cn(dialogVariants({ size, className }))}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  ),
)
DialogContent.displayName = 'DialogContent'

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex items-start justify-between gap-4 px-6 pt-6', className)} {...props} />
}

/** The modal title, an `h2` in `text-h2` (§7.13). */
export const DialogTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Title asChild>
      <h2 ref={ref} className={cn('text-h2 text-navy-950', className)} {...props} />
    </DialogPrimitive.Title>
  ),
)
DialogTitle.displayName = 'DialogTitle'

export function DialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-6 text-body', className)} {...props} />
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div data-dialog-footer="" className={cn('flex justify-end gap-3 px-6 pb-6', className)} {...props} />
}
